import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";
import { Button } from "@/components/flowly/infra/Button/Button";

// Sample images for demonstration
const SAMPLE_BAD_IMAGE = "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&q=80&w=400";
const SAMPLE_GOOD_IMAGE = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=400";
const SAMPLE_PROFILE_IMAGE = "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&q=80&w=400";

export const ImageUploadGuide = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const notSuitablePoints = t.imageUploadGuide.notSuitableShort.split("|");
  const suitablePoints = t.imageUploadGuide.suitableShort.split("|");
  const profilePoints = t.imageUploadGuide.profilePointsList.split("|");

  return (
    <>
      {/* Toggle Button - small, positioned above */}
      <Button
        variant="ghost"
        size="xs"
        onClick={() => setIsOpen(true)}
        className="text-[11px] underline-offset-2 hover:underline !p-0"
      >
        {t.imageUploadGuide.buttonText}
      </Button>

      <ModalCard isOpen={isOpen} onClose={() => setIsOpen(false)} maxWidth="max-w-4xl" className="w-[95vw]">
        <div>
          {/* Three Cards Side by Side - Order: Right=Not Suitable, Middle=Suitable, Left=Optional */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            {/* Not Suitable Card (Right in RTL = first in grid) */}
            <div className="rounded-xl border-2 border-red-500/30 bg-red-500/5 overflow-hidden">
              {/* Image with overlay text */}
              <div className="relative aspect-square">
                <img
                  src={SAMPLE_BAD_IMAGE}
                  alt="Not suitable example"
                  className="w-full h-full object-cover filter brightness-75"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-0 right-0 text-center">
                  <span className="text-white font-bold text-sm drop-shadow-lg">
                    {t.imageUploadGuide.notSuitablePhoto}
                  </span>
                </div>
                <div className="absolute top-3 left-3">
                  <span className="text-2xl">&#x274C;</span>
                </div>
              </div>

              {/* Points below image */}
              <div className="p-3 space-y-1.5">
                {notSuitablePoints.map((point, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="text-red-500 text-xs">&#x274C;</span>
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Suitable Card (Middle) */}
            <div className="rounded-xl border-2 border-green-500/30 bg-green-500/5 overflow-hidden">
              {/* Image with overlay text */}
              <div className="relative aspect-square">
                <img
                  src={SAMPLE_GOOD_IMAGE}
                  alt="Suitable example"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-0 right-0 text-center">
                  <span className="text-white font-bold text-sm drop-shadow-lg">
                    {t.imageUploadGuide.properlyPhotographed}
                  </span>
                </div>
                <div className="absolute top-3 left-3">
                  <span className="text-2xl">&#x2705;</span>
                </div>
              </div>

              {/* Points below image */}
              <div className="p-3 space-y-1.5">
                {suitablePoints.map((point, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="text-green-500 text-xs">&#x2705;</span>
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Optional Reference Card (Left in RTL = last in grid) */}
            <div className="rounded-xl border-2 border-blue-500/30 bg-blue-500/5 overflow-hidden">
              {/* Image with overlay text */}
              <div className="relative aspect-square">
                <img
                  src={SAMPLE_PROFILE_IMAGE}
                  alt="Optional reference example"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                {/* Optional Badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                    {t.imageUploadGuide.optionalBadge}
                  </span>
                </div>
                <div className="absolute bottom-3 left-0 right-0 text-center">
                  <span className="text-white font-bold text-sm drop-shadow-lg">
                    {t.imageUploadGuide.profileSideView}
                  </span>
                </div>
              </div>

              {/* Checklist below image */}
              <div className="p-3 space-y-1.5">
                {profilePoints.map((point, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="text-blue-500 text-xs">&#x2713;</span>
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Disclaimers at bottom */}
          <div className="mt-4 pt-3 border-t border-border space-y-1">
            <p className="text-[11px] text-muted-foreground/80">
              {t.imageUploadGuide.aiDisclaimer}
            </p>
            <p className="text-[11px] text-muted-foreground/80">
              {t.imageUploadGuide.hebrewTextDisclaimer}
            </p>
          </div>
        </div>
      </ModalCard>
    </>
  );
};
